import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ComponentCanDeactivate } from 'src/app/common/can-deactivate/can-deactivate';
import { Organisation } from 'src/app/service/organisation/organisation';
import { OrganisationService } from '../../service/organisation/organisation.service';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ConfirmDialogService } from '../../service/common/confirm-dialog/confirm-dialog.service';
import { Router } from '@angular/router';
import { ZuweisungService } from '../../service/zuweisung/zuweisung.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'ufla-netzbetreiber-detail',
    templateUrl: './netzbetreiber-detail.component.html',
    styleUrls: [
        './netzbetreiber-detail.component.scss',
    ],
    standalone: false,
})
export class NetzbetreiberDetailComponent extends ComponentCanDeactivate {
    private dialogRef = inject<MatDialogRef<NetzbetreiberDetailComponent>>(MatDialogRef);
    private organisationService = inject(OrganisationService);
    private zuweisungService = inject(ZuweisungService);
    private snackBar = inject(MatSnackBar);
    private dialog = inject(MatDialog);
    private confirmDialogService = inject(ConfirmDialogService);
    private router = inject(Router);
    data = inject(MAT_DIALOG_DATA);

    public createNew = false;
    public orgId = '';
    public netzbetreiber: Organisation;
    public originalNetzbetreiber = new Organisation();
    selectable = true;
    removable = true;
    addOnBlur = true;
    readonly separatorKeysCodes: number[] = [
        ENTER,
        COMMA,
    ];

  @ViewChild('netzbetreiberForm', { static: false }) public netzbetreiberForm: NgForm;
  @ViewChild('errorDialog', { static: false }) errorDialog: TemplateRef<any>;
  @ViewChild('unableToLoadOrgDialog', { static: false }) unableToLoadDialog: TemplateRef<any>;

  constructor() {
      super();
      const data = this.data;

      this.createNew = data.createNew;
      this.orgId = data.orgId;

      if (this.netzbetreiberForm) {
          this.netzbetreiberForm.reset();
      }

      if (this.createNew) {

          this.netzbetreiber = new Organisation();
      } else {
          this.loadNetzbetreiber();
      }

  }

  canDeactivate(): boolean {
      return !this.netzbetreiberForm.dirty;
  }

  public cancel() {
      this.dialogRef.close();
  }

  public loadNetzbetreiber() {
      this.organisationService.getOrganisation(this.orgId).pipe(take(1))
          .subscribe(
              org => {
                  this.netzbetreiber = org;
                  this.originalNetzbetreiber = JSON.parse(JSON.stringify(this.netzbetreiber));
              },
              _ => this.dialog.open(this.unableToLoadDialog));
  }

  public async saveNetzbetreiber() {
      const netzbetreiberToBeSent = JSON.parse(JSON.stringify(this.netzbetreiber));

      if (this.createNew) {
          this.organisationService.createOrganisation(netzbetreiberToBeSent)
              .then(_resp => {
                  this.snackBar.open('Netzbetreiber angelegt', 'Ok', {
                      duration: 2000,
                  });
              })
              .catch(_ => {
                  this.dialog.open(this.errorDialog);
              });
      } else {
          this.organisationService.updateOrganisation(this.orgId, netzbetreiberToBeSent)
              .then(_resp => {
                  this.snackBar.open('Änderungen gespeichert', 'Ok', {
                      duration: 2000,
                  });
              })
              .catch(_ => {
                  this.dialog.open(this.errorDialog);
              });
      }

      this.netzbetreiberForm.form.markAsPristine();
      this.dialogRef.close();
  }

  public resetForm() {
      this.loadNetzbetreiber();
      this.netzbetreiberForm.form.markAsPristine();
  }

  addNnvnbEicNumme(event: MatChipInputEvent) {
      const input = event.input;
      const value = event.value;

      if ((value || '').trim()) {
          if (this.netzbetreiber.eicNummerNvnbs === undefined) {
              this.netzbetreiber.eicNummerNvnbs = [];
          }
          this.netzbetreiber.eicNummerNvnbs.push(value.trim());
      }

      if (input) {
          input.value = '';
      }
  }

  removeNnvnbEicNummer(nnvnbEicNummer: string) {
      this.netzbetreiber.eicNummerNvnbs = this.netzbetreiber.eicNummerNvnbs.filter(value => value !== nnvnbEicNummer);
  }

  async checkOnlyOnWhenUserConfirms($event: MatSlideToggleChange) {
      if ($event.checked && await this.confirmDialogService.showDialog(
          'Deaktivierte Netzbetreiber werden nicht mehr als Empfänger neuer Vorgänge berücksichtigt.' +
      ' Offene Vorgänge können aber weiter bearbeitet werden.', 'Wirklich deaktivieren?',
          'primary', 'Ja', 'Nein')) {
      } else {
      // user has rejected / does not confirmation, reset
          if (this.netzbetreiber) {
              this.netzbetreiber.zuweisungDeaktiviert = false;
          }
      }
  }

  public canDelete(netzbetreiberId: string): boolean {
      return Number(netzbetreiberId) > 2;
  }

  async delete(netzbetreiber: Organisation) {
      const countOfZugewiesen = await this.zuweisungService.countZugewiesenAndNotGelesen(netzbetreiber.id);
      if (await this.confirmDialogService.showDialog('Sind Sie sicher, dass Sie diesen Netzbetreiber ' +
      '(mit ' + countOfZugewiesen + ' nicht erledigten Zuweisungen) löschen möchten? Netzbetreiber, die gelöscht' +
      ' wurden, können nicht wiederhergestellt werden.', 'Hinweis zum Löschen',
      'primary', 'Ja', 'Nein')) {
          await this.organisationService.deleteOrganisation(String(netzbetreiber.id));
          this.cancel(); // no update or save after deletion
          await this.router.navigate([
              'netzbetreiber',
          ]);
      }
  }
}
